'use client'

import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { Container } from '@/components'
import { Media } from '@/components/Media'
import { Breadcrumbs } from '@/components/ui/organisms/Breadcrumbs'
import type { Page } from '@/payload-types'
import { cn } from '@/utilities/cn'

type HeroTextLine = {
  color?: string | null
  id?: string | null
  text: string
}

type MovieHeroWithCounterProps = Page['hero'] & {
  breadcrumbs?: Array<{
    label: string
    url: string
  }> | null
  breadcrumbsVariant?: 'default' | 'light' | 'dark'
}

const TextLine: FC<{ line: HeroTextLine }> = ({ line }) => (
  <span
    className="gpf-movie-hero-counter__line"
    style={line.color ? { color: `var(--gpf-color-${line.color})` } : undefined}
  >
    {line.text}
  </span>
)

const useCountUp = (target: number, active: boolean, duration = 2000) => {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!active) return

    let frame: number
    const start = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      setValue(Math.round(target * progress))

      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      }
    }

    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
  }, [active, target, duration])

  return value
}

export const MovieHeroWithCounter: FC<MovieHeroWithCounterProps> = ({
  media,
  topTextLines,
  counterValue,
  counterColor,
  counterShowPlus,
  bottomTextLines,
  breadcrumbs,
  breadcrumbsVariant = 'default',
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  const count = useCountUp(counterValue ?? 0, isInView)

  return (
    <div>
      {breadcrumbs && (
        <Breadcrumbs
          variant={breadcrumbsVariant !== 'default' ? breadcrumbsVariant : 'light'}
          absolute={true}
          items={breadcrumbs}
        />
      )}
      <Container>
        <div className="gpf-movie-hero-counter" ref={sectionRef}>
          {media && typeof media === 'object' && (
            <div className="gpf-movie-hero-counter__media">
              <Media
                fill={true}
                priority={true}
                resource={media}
                imgClassName="gpf-movie-hero-counter__media-item"
                videoClassName="gpf-movie-hero-counter__media-item"
              />
            </div>
          )}
          <div className="gpf-movie-hero-counter__content">
            {topTextLines && topTextLines.length > 0 && (
              <h1
                className={cn(
                  'gpf-movie-hero-counter__text-block gpf-movie-hero-counter__text-block--top',
                )}
              >
                {topTextLines.map((line) => (
                  <TextLine key={line.id} line={line} />
                ))}
              </h1>
            )}
            {typeof counterValue === 'number' && (
              <div
                className="gpf-movie-hero-counter__counter"
                style={
                  counterColor
                    ? { color: `var(--gpf-color-${counterColor})` }
                    : undefined
                }
              >
                {count}
                {counterShowPlus && (
                  <span className="gpf-movie-hero-counter__counter-plus">+</span>
                )}
              </div>
            )}
            {bottomTextLines && bottomTextLines.length > 0 && (
              <p
                className={cn(
                  'gpf-movie-hero-counter__text-block ',
                  'gpf-movie-hero-counter__text-block--bottom',
                )}
              >
                {bottomTextLines.map((line) => (
                  <TextLine key={line.id} line={line} />
                ))}
              </p>
            )}
          </div>
        </div>
      </Container>
    </div>
  )
}
